import type { CSSProperties } from 'react';

import {
  backgroundStyle,
  type ColumnLookExtra,
  gradientCss,
  gradientText,
  isHex,
  patternLayer,
} from '@/entities/appearance';
import type { Column, ColumnAppearance } from '@/entities/board/types';
import { contrastText } from '@/shared/lib/color';

import styles from './ColumnAppearance.module.css';

/** CSS module classes shared by the real column and the appearance preview. */
export const columnAppearanceStyles = {
  frame: styles.frame,
  head: styles.head,
  /** Parts drawn directly on the column (header, empty text, footer), not cards. */
  chrome: styles.chrome,
  /** The card counter pill in the header. */
  badge: styles.badge,
} as const;

export interface ColumnProps {
  'data-appearance'?: ColumnAppearance;
  'data-fg'?: 'true';
  'data-adv-header'?: 'true';
  'data-adv-border'?: 'true';
  'data-adv-body'?: 'true';
  'data-bg'?: 'true';
  'data-title-color'?: 'true';
  'data-text-color'?: 'true';
  style?: CSSProperties;
}

type ColumnLike = Pick<Column, 'color' | 'appearance_mode'> & {
  appearance?: ColumnLookExtra | null;
  background_image_url?: string | null;
};

/**
 * Semantic column look -> DOM hooks. The primary color is `--column-color`, the style is
 * `data-appearance`; gradient, pattern, advanced colors and the background come from the
 * typed extras. The look of each style is plain CSS (`ColumnAppearance.module.css`).
 * Without a color (and without extras) the column keeps the neutral look.
 */
export function columnProps(column: ColumnLike): ColumnProps {
  const props: ColumnProps = {};
  const vars: Record<string, string> = {};
  const extra = column.appearance ?? null;
  const mode = column.appearance_mode;
  const color = isHex(column.color) ? column.color : null;
  let foreground: string | null = null;

  if (mode === 'gradient' && extra?.gradient) {
    const css = gradientCss(extra.gradient);
    if (css) {
      vars['--column-gradient'] = css;
      foreground = gradientText(extra.gradient);
      props['data-appearance'] = mode;
    }
  } else if (mode === 'pattern' && extra?.pattern) {
    const layer = patternLayer(extra.pattern);
    if (layer) {
      vars['--column-pattern'] = layer.image;
      vars['--column-pattern-size'] = layer.size;
      if (layer.background) {
        vars['--column-pattern-bg'] = layer.background;
        foreground = contrastText(layer.background);
      }
      props['data-appearance'] = mode;
    }
  } else if (color) {
    vars['--column-color'] = color;
    props['data-appearance'] = mode;
    if (mode === 'solid') foreground = contrastText(color);
  }

  if (extra && !extra.one_color && extra.advanced) {
    const { header, border, body } = extra.advanced;
    if (isHex(header)) {
      vars['--column-header'] = header;
      props['data-adv-header'] = 'true';
    }
    if (isHex(border)) {
      vars['--column-border'] = border;
      props['data-adv-border'] = 'true';
    }
    if (isHex(body)) {
      vars['--column-body'] = body;
      props['data-adv-body'] = 'true';
      foreground = contrastText(body);
    }
  }
  if (foreground) {
    vars['--column-fg'] = foreground;
    props['data-fg'] = 'true';
  }

  // Explicit text colors (typed `#rrggbb`, validated again here) win over the derived foreground.
  if (extra) {
    if (isHex(extra.title_color)) {
      vars['--column-title-color'] = extra.title_color;
      props['data-title-color'] = 'true';
    }
    if (isHex(extra.text_color)) {
      vars['--column-text-color'] = extra.text_color;
      props['data-text-color'] = 'true';
    }
    // The inline "add a card" form: its own background / text / border (placeholder derives from the text).
    for (const [field, variable] of [
      ['form_background', '--column-form-bg'],
      ['form_text', '--column-form-fg'],
      ['form_border', '--column-form-border'],
    ] as const) {
      const value = extra[field];
      if (isHex(value)) vars[variable] = value;
    }
    // The description sits on its own surface; its text follows the surface unless chosen.
    const surface = isHex(extra.description_surface) ? extra.description_surface : null;
    const descriptionFg = isHex(extra.description_color)
      ? extra.description_color
      : surface
        ? contrastText(surface)
        : null;
    if (surface) vars['--column-desc-bg'] = surface;
    if (descriptionFg) {
      vars['--column-desc-fg'] = descriptionFg;
      vars['--column-desc-link'] = descriptionFg;
      vars['--column-desc-border'] = `color-mix(in srgb, ${descriptionFg} 28%, transparent)`;
    }
  }

  const background = backgroundStyle(extra?.background, column.background_image_url ?? null);
  if (Object.keys(background.style).length > 0) {
    props['data-bg'] = 'true';
    return { ...props, style: { ...(vars as CSSProperties), ...background.style } };
  }
  return Object.keys(vars).length > 0 ? { ...props, style: vars } : props;
}

/** Kept for the basic one-color call sites: color + style only. */
export function appearanceProps(
  color: string | null,
  mode: ColumnAppearance,
): { 'data-appearance'?: ColumnAppearance; style?: CSSProperties } {
  if (!color) return {};
  return { 'data-appearance': mode, style: { '--column-color': color } as CSSProperties };
}
